import { useCallback, useEffect } from "react";
import { fetchContactsDirectory } from "@/lib/contactsDirectoryClient";
import { CONTACT_PATH_OPTIONS, TOPIC_OPTIONS, createEmptyExternalForm, type MatchResolution } from "./adminContactsDirectoryShared";
import { formatUnknownError, hasAtLeastTwoWords, isLikelyValidEmail, normalizeEmail, normalizePhone, parseTopicsText } from "./adminContactsDirectoryUtils";
import { withCopiedAllNonEmptyIncomingFieldsToMergedDraft, withCopiedIncomingFieldToMergedDraft, withUpdatedImportMatchResolution } from "./adminContactsImportMatchState";
import { useAdminContactsDirectoryDerived } from "./useAdminContactsDirectoryDerived";
import { useAdminContactsReporting } from "./useAdminContactsReporting";
import { useAdminContactsDirectoryActions } from "./useAdminContactsDirectoryActions";
import { useAdminContactsImportWorkflow } from "./useAdminContactsImportWorkflow";
import { useAdminContactsSubordinateAssignment } from "./useAdminContactsSubordinateAssignment";
import { useAdminContactsSaveFlow } from "./useAdminContactsSaveFlow";

export function useAdminContactsDirectoryViewModel(params: {
  organizationId: string;
  sb: any;
  authToken?: string;
  onToast?: (message: string, variant?: "ok" | "err") => void;
  state: any;
}) {
  const { organizationId, sb, authToken, onToast, state: s } = params;
  const {
    hasAutoOpenedGeneralFallback,
    locationsOrgColumn,
    setExternal,
    setUserDir,
    setOrgUsers,
    setGeneralFallback,
    setGeneralFallbackChoice,
    setDirectoryMode,
    setHasAutoOpenedGeneralFallback,
    setLocationsOrgColumn,
    setLocations,
  } = s;
  const toast = useCallback((message: string, variant: "ok" | "err" = "ok") => onToast?.(message, variant), [onToast]);
  const missingGeneralSupportFallback = !s.generalFallback.source_id;

  const derived = useAdminContactsDirectoryDerived({
    external: s.external, userDir: s.userDir, orgUsers: s.orgUsers, directoryListingFilter: s.directoryListingFilter, showDeactivatedExternal: s.showDeactivatedExternal,
    externalSearch: s.externalSearch, externalPage: s.externalPage, externalVisibleCount: s.externalVisibleCount, extFormTopics: s.extForm.topics, extFormContactPath: s.extForm.contact_path,
    parseTopicsText, normalizeEmail, contactPathOptions: CONTACT_PATH_OPTIONS, topicOptions: TOPIC_OPTIONS,
  });

  const load = useCallback(async () => {
    try {
      const out = await fetchContactsDirectory(organizationId);
      setExternal(out.external);
      setUserDir(out.user_directory);
      setOrgUsers(out.org_users ?? []);
      setGeneralFallback(out.general_fallback);
      setGeneralFallbackChoice(out.general_fallback.source && out.general_fallback.source_id ? `${out.general_fallback.source}:${out.general_fallback.source_id}` : "");
      if (!hasAutoOpenedGeneralFallback) {
        if (!out.general_fallback.source_id) setDirectoryMode("general_support");
        setHasAutoOpenedGeneralFallback(true);
      }
      let locsQuery;
      if (locationsOrgColumn === "organization_id") locsQuery = await sb.from("locations").select("id, name").eq("organization_id", organizationId).order("name", { ascending: true });
      else if (locationsOrgColumn === "org_id") locsQuery = await sb.from("locations").select("id, name").eq("org_id", organizationId).order("name", { ascending: true });
      else {
        const byOrganizationId = await sb.from("locations").select("id, name").eq("organization_id", organizationId).order("name", { ascending: true });
        if (!byOrganizationId.error) { setLocationsOrgColumn("organization_id"); locsQuery = byOrganizationId; }
        else { const byOrgId = await sb.from("locations").select("id, name").eq("org_id", organizationId).order("name", { ascending: true }); if (!byOrgId.error) setLocationsOrgColumn("org_id"); locsQuery = byOrgId; }
      }
      const { data: locs, error } = locsQuery;
      if (error) throw error;
      setLocations((locs ?? []) as { id: string; name: string }[]);
    } catch (e: unknown) {
      toast(formatUnknownError(e), "err");
    }
  }, [organizationId, sb, toast, hasAutoOpenedGeneralFallback, locationsOrgColumn, setDirectoryMode, setExternal, setGeneralFallback, setGeneralFallbackChoice, setHasAutoOpenedGeneralFallback, setLocations, setLocationsOrgColumn, setOrgUsers, setUserDir]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { s.setExternalPage(1); }, [s.externalVisibleCount, s.directoryListingFilter, s.showDeactivatedExternal, s.externalSearch]);
  useEffect(() => { if (s.externalPage > derived.externalTotalPages) s.setExternalPage(derived.externalTotalPages); }, [s.externalPage, derived.externalTotalPages, s.setExternalPage]);
  useEffect(() => { s.setExpandedContactKeys([]); }, [s.externalSearch, s.directoryListingFilter, s.showDeactivatedExternal, s.externalVisibleCount, s.externalPage]);

  const reporting = useAdminContactsReporting({
    organizationId, directoryMode: s.directoryMode, toast, userDir: s.userDir, external: s.external, hideAlreadyAssignedSubordinates: s.hideAlreadyAssignedSubordinates, subordinateManagerKey: s.subordinateManagerKey,
  });

  const saveFlow = useAdminContactsSaveFlow({
    organizationId, toast, formatUnknownError, extForm: s.extForm, setExtForm: s.setExtForm, editingUserRow: s.editingUserRow, setEditingUserRow: s.setEditingUserRow,
    userManagerProfileIdSet: derived.userManagerProfileIdSet, setUserFormBusy: s.setUserFormBusy, isLikelyValidEmail, hasAtLeastTwoWords, load, createEmptyExternalForm, parseTopicsText, setRoleGuidance: s.setRoleGuidance,
  });

  const actions = useAdminContactsDirectoryActions({
    organizationId,
    setDirectoryMode: s.setDirectoryMode,
    authToken,
    inviteEmail: s.inviteEmail,
    inviteRole: s.inviteRole,
    inviteTarget: s.inviteTarget,
    setInviteBusy: s.setInviteBusy,
    setInviteTarget: s.setInviteTarget,
    setInviteEmail: s.setInviteEmail,
    migrateTarget: s.migrateTarget, migrateEmail: s.migrateEmail, migrateProfileId: s.migrateProfileId, setMigrateTarget: s.setMigrateTarget, setMigrateEmail: s.setMigrateEmail, setMigrateProfileId: s.setMigrateProfileId,
    extForm: s.extForm, setExtForm: s.setExtForm, setEditingUserRow: s.setEditingUserRow, isLikelyValidEmail, formatUnknownError, createEmptyExternalForm, load,
    pathSource: s.pathSource, pathTarget: s.pathTarget, setPathSource: s.setPathSource, setPathTarget: s.setPathTarget, topicSource: s.topicSource, topicTarget: s.topicTarget, setTopicSource: s.setTopicSource, setTopicTarget: s.setTopicTarget,
    generalFallbackChoice: s.generalFallbackChoice, setGeneralFallbackChoice: s.setGeneralFallbackChoice, setGeneralFallbackBusy: s.setGeneralFallbackBusy, toast,
  });

  function updateImportMatchResolution(key: string, resolution: MatchResolution) { s.setImportMatches((prev: any) => withUpdatedImportMatchResolution(prev, key, resolution)); }
  function copyIncomingFieldToMergedDraft(key: string, field: any) { s.setImportMatches((prev: any) => withCopiedIncomingFieldToMergedDraft(prev, key, field)); }
  function copyAllNonEmptyIncomingFieldsToMergedDraft(key: string) { s.setImportMatches((prev: any) => withCopiedAllNonEmptyIncomingFieldsToMergedDraft(prev, key)); }

  const importWorkflow = useAdminContactsImportWorkflow({
    organizationId, external: s.external, normalizeEmail, normalizePhone, formatUnknownError, toast, load, setPendingImportRows: s.setPendingImportRows,
    setImportMatches: s.setImportMatches, pendingImportRows: s.pendingImportRows, importMatches: s.importMatches, setApplyingImport: s.setApplyingImport,
  });

  const subordinateWorkflow = useAdminContactsSubordinateAssignment({
    organizationId, subordinateManagerKey: s.subordinateManagerKey, selectedSubordinateKeys: s.selectedSubordinateKeys, reportingTree: reporting.reportingTree, externalDirectoryById: reporting.externalDirectoryById,
    orgProfileIdByEmail: derived.orgProfileIdByEmail, reportingNodeByKey: reporting.reportingNodeByKey, normalizeEmail, external: s.external, userDir: s.userDir, reportingPickerLabel: reporting.reportingPickerLabel,
    formatUnknownError, toast, setAssigningSubordinates: s.setAssigningSubordinates, setLastSubordinateAssignment: s.setLastSubordinateAssignment, setSelectedSubordinateKeys: s.setSelectedSubordinateKeys, load,
    refreshReportingTree: reporting.refreshReportingTree,
  });

  const downstreamCapBadge = (level: number | null | undefined) =>
    level === null || level === undefined ? <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-400">Follows level</span> : <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] text-indigo-800 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200">Cap L{level}</span>;

  return {
    ...s, ...derived, ...reporting, ...actions, ...saveFlow, ...importWorkflow, ...subordinateWorkflow, updateImportMatchResolution, copyIncomingFieldToMergedDraft,
    copyAllNonEmptyIncomingFieldsToMergedDraft, missingGeneralSupportFallback, downstreamCapBadge, parseTopicsText, createEmptyExternalForm,
  };
}
